import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import { AppLayout } from '@/components/layout/AppLayout';

// ── Eagerly-loaded (always needed on first paint) ─────────────────────────
import { LoginPage } from '@/pages/LoginPage';
// Eager too: this is the first paint after the provider redirects back, so a
// lazy chunk fetch would sit between the user and their session.
import { AuthCallbackPage } from '@/pages/AuthCallbackPage';

// ── Lazily-loaded pages (route-level code splitting) ──────────────────────
const DashboardPage      = lazy(() => import('@/pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const CommandCenterPage  = lazy(() => import('@/pages/CommandCenterPage').then(m => ({ default: m.CommandCenterPage })));
const ThreatsPage        = lazy(() => import('@/pages/ThreatsPage').then(m => ({ default: m.ThreatsPage })));
const EndpointsPage      = lazy(() => import('@/pages/EndpointsPage').then(m => ({ default: m.EndpointsPage })));
const EndpointDetailPage = lazy(() => import('@/pages/EndpointDetailPage').then(m => ({ default: m.EndpointDetailPage })));
const AiSessionsPage     = lazy(() => import('@/pages/AiSessionsPage').then(m => ({ default: m.AiSessionsPage })));
const AiSessionDetailPage = lazy(() => import('@/pages/AiSessionDetailPage').then(m => ({ default: m.AiSessionDetailPage })));
const AnalyticsPage      = lazy(() => import('@/pages/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const SettingsPage       = lazy(() => import('@/pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const PoliciesPage       = lazy(() => import('@/pages/PoliciesPage').then(m => ({ default: m.PoliciesPage })));
const AuditLogPage       = lazy(() => import('@/pages/AuditLogPage').then(m => ({ default: m.AuditLogPage })));
const OwaspTop10Page     = lazy(() => import('@/pages/OwaspTop10Page').then(m => ({ default: m.OwaspTop10Page })));
const InventoryPage      = lazy(() => import('@/pages/InventoryPage').then(m => ({ default: m.InventoryPage })));
const AgentsPage         = lazy(() => import('@/pages/AgentsPage').then(m => ({ default: m.AgentsPage })));
const McpsPage           = lazy(() => import('@/pages/McpsPage').then(m => ({ default: m.McpsPage })));
const SkillsPage         = lazy(() => import('@/pages/SkillsPage').then(m => ({ default: m.SkillsPage })));
const PluginsPage        = lazy(() => import('@/pages/PluginsPage').then(m => ({ default: m.PluginsPage })));
const McpConsolePage     = lazy(() => import('@/pages/McpConsolePage').then(m => ({ default: m.McpConsolePage })));
const ObservabilityPage  = lazy(() => import('@/pages/ObservabilityPage').then(m => ({ default: m.ObservabilityPage })));

// ── Disabled feature routes ───────────────────────────────────────────────
// The underlying pages still exist on disk and remain importable, but the
// router serves a "Feature Disabled" placeholder for the canonical paths.
// To re-enable a section, swap its element back to the real lazy import below.
const FeatureDisabledPage = lazy(() => import('@/pages/FeatureDisabledPage').then(m => ({ default: m.FeatureDisabledPage })));

// ── Page suspense boundary ────────────────────────────────────────────────
function PageLoader() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
    </div>
  );
}

// ── Protected route wrapper ───────────────────────────────────────────────
function ProtectedRoute() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return (
    <AppLayout>
      <Suspense fallback={<PageLoader />}>
        <Outlet />
      </Suspense>
    </AppLayout>
  );
}

// ── Router ────────────────────────────────────────────────────────────────
export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  // Must stay a top-level sibling of /login, not a child of '/': ProtectedRoute
  // would otherwise bounce it to /login before the code exchange ever runs.
  {
    path: '/auth/callback',
    element: <AuthCallbackPage />,
  },
  {
    path: '/',
    element: <ProtectedRoute />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },

      // ── Active routes (sidebar-visible) ──
      { path: 'dashboard',           element: <DashboardPage /> },
      { path: 'owasp-top10',         element: <OwaspTop10Page /> },
      { path: 'inventory',           element: <InventoryPage /> },
      { path: 'agents',              element: <AgentsPage /> },
      { path: 'mcps',                element: <McpsPage /> },
      { path: 'skills',              element: <SkillsPage /> },
      { path: 'plugins',             element: <PluginsPage /> },
      { path: 'observability',       element: <ObservabilityPage /> },
      { path: 'realtime-sessions',   element: <Navigate to="/ai-sessions" replace /> },
      { path: 'threats',             element: <ThreatsPage /> },
      { path: 'analytics',           element: <AnalyticsPage /> },
      { path: 'audit-log',           element: <AuditLogPage /> },
      { path: 'policies',            element: <PoliciesPage /> },
      { path: 'mcp-console',         element: <McpConsolePage /> },
      { path: 'settings',            element: <SettingsPage /> },

      // ── Sub-detail routes (drill-down from active sections) ──
      { path: 'endpoints',           element: <EndpointsPage /> },
      { path: 'endpoints/:id',       element: <EndpointDetailPage /> },
      // Endpoint groups now live as a tab on /endpoints — redirect old bookmarks/links.
      { path: 'endpoint-groups',     element: <Navigate to="/endpoints?tab=groups" replace /> },
      { path: 'ai-sessions',         element: <AiSessionsPage /> },
      { path: 'ai-sessions/:id',     element: <AiSessionDetailPage /> },

      // ── Disabled routes (feature gated, page sources retained on disk) ──
      { path: 'compliance',          element: <FeatureDisabledPage featureName="Compliance" /> },
      { path: 'process-trace',       element: <FeatureDisabledPage featureName="Process Trace" /> },
      { path: 'mitigation',          element: <FeatureDisabledPage featureName="Mitigation Playbooks" /> },
      { path: 'threat-intel',        element: <FeatureDisabledPage featureName="Threat Intelligence" /> },
      { path: 'agent-deploy',        element: <FeatureDisabledPage featureName="Agent Deployment" /> },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
